import { CLIENT_PAGES } from '@/constants/clientPortalPages'
import { isSelectableScopeKey, normalizeClientId } from '@/utilities/auth/authHelper'
import { getCachedTrustedIdentityScopes } from '@/utilities/auth/trustedIdentityScopes'
import type { IdentityScopes } from '@/utilities/auth/identityScopeRoles'
import { buildDashboardFilterSearchQuery } from '@/utils/dashboard/dashboardUrlFilters'
import { canSelectOrgForPrimaryPartner } from '@/utilities/dashboard/orgProgramAuthorization'

/**
 * Build the program-dashboard redirect URL, preserving existing filter/query params.
 * Uses the already-authorized partner selected by the shared Client Portal context.
 * Drops `org` since that is org-dashboard context only.
 */
export function buildProgramDashboardRedirectUrl(
  searchParams: URLSearchParams | { toString(): string },
  partnerId: string | null | undefined,
): string {
  const params = new URLSearchParams(searchParams.toString())
  params.delete('org')

  if (partnerId) {
    params.set('partners', normalizeClientId(partnerId))
  } else {
    params.delete('partners')
  }

  const query = params.toString()
  return query ? `${CLIENT_PAGES.programDashboard}?${query}` : CLIENT_PAGES.programDashboard
}

/**
 * Build the query string for a program-dashboard client switch: always set `partners`,
 * optionally with a complete date/view filter set. Keeps partner URL ownership here
 * rather than inlining it inside the date-filter hook.
 */
export function buildProgramClientSwitchSearchQuery(
  partnerId: string,
  dateFilters: { startDate: Date; endDate: Date; viewValue: string } | null,
): string {
  const nextParams = new URLSearchParams()
  nextParams.set('partners', partnerId)
  if (!dateFilters) return nextParams.toString()
  return buildDashboardFilterSearchQuery(
    dateFilters.startDate,
    dateFilters.endDate,
    dateFilters.viewValue,
    nextParams.toString(),
  )
}

/**
 * True when `org` is an authorized primary partner id for the user's identity scopes.
 * Per the org/program URL contract, `org` must be the organization's primary partner
 * (is_primary), not an arbitrary program the user can access.
 * Ash admins often only have the `*` scope and pick one org at a time via client
 * selection, so any selectable `org` id is treated as authorized for them.
 * Returns null while scopes are not yet available.
 */
export function isAuthorizedOrgParam(
  orgId: string,
  scopesOverride?: IdentityScopes | null,
): boolean | null {
  if (!isSelectableScopeKey(orgId)) return false
  const scopes = scopesOverride === undefined ? getCachedTrustedIdentityScopes() : scopesOverride
  if (!scopes) return null

  return canSelectOrgForPrimaryPartner(scopes, orgId)
}

/** Resolve a program context only from partner IDs returned by `/partners`. */
export function resolveProgramPartnerIdFromAllowedPartners(
  allowedPartnerIds: readonly string[],
  partnersFromUrl: string | null | undefined,
  selectedClientId: string | null | undefined,
): string | null {
  const allowed = new Set(allowedPartnerIds.map(normalizeClientId))
  const fromUrl = partnersFromUrl?.trim() ? normalizeClientId(partnersFromUrl.trim()) : null
  if (fromUrl && allowed.has(fromUrl)) return fromUrl

  const fromSelection = selectedClientId?.trim() ? normalizeClientId(selectedClientId.trim()) : null
  if (fromSelection && allowed.has(fromSelection)) return fromSelection

  return allowedPartnerIds[0] ? normalizeClientId(allowedPartnerIds[0]) : null
}
